import { useState, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import { 
  calculateMemberSalary,
  getRateForRole,
  getRoleProjectYear,
  getProjectYear
} from '../../../utils/rateUtils';

export const useStaffingCalculations = (formData, outcomeData, itRoster, rateData) => {
  const [calculationCache, setCalculationCache] = useState(new Map());

  // Memoize helper functions
  const getInitialStartMonth = useCallback(() => {
    if (outcomeData?.start_date) {
      return dayjs(outcomeData.start_date).format('YYYY-MM');
    }
    if (formData?.start_month) {
      return formData.start_month;
    }
    return dayjs().format('YYYY-MM');
  }, [outcomeData?.start_date, formData?.start_month]);

  const getInitialAdjustedMonths = useCallback(() => {
    if (outcomeData?.start_date && outcomeData?.end_date) {
      const startDate = dayjs(outcomeData.start_date);
      const endDate = dayjs(outcomeData.end_date);
      return Math.max(1, endDate.diff(startDate, 'month') + 1);
    }
    return formData?.adjustedMonths || 6;
  }, [formData?.adjustedMonths, outcomeData?.start_date, outcomeData?.end_date]);

  // Calculate CAPEX/OPEX totals
  const calculateCapexOpexTotals = useMemo(() => {
    if (!formData?.milestones) return { totalCapex: 0, totalOpex: 0 };

    let totalCapex = 0;
    let totalOpex = 0;

    // Calculate role costs
    formData.milestones.forEach(milestone => {
      (milestone.roles || []).forEach(role => {
        const monthlyRate = role.rate || 0;
        const allocation = Math.max(0, Math.min(100, role.allocation || 0)) / 100;
        const months = Math.max(1, role.overlap_months || 1);
        const roleCost = monthlyRate * allocation * months;

        if (isNaN(roleCost) || roleCost < 0) return;

        if (role.capex_classification === 'CAPEX') {
          totalCapex += roleCost;
        } else {
          totalOpex += roleCost;
        }
      });
    });

    // Add fixed costs
    (formData.fixedCosts || []).forEach(cost => {
      const startDate = dayjs(cost.start_date + '-01');
      const endDate = dayjs(cost.end_date + '-01');
      const duration = Math.max(1, endDate.diff(startDate, 'month') + 1);
      const fixedCost = (cost.monthlyCost || 0) * duration;

      if (isNaN(fixedCost) || fixedCost < 0) return;

      if ((cost.capex_classification || 'OPEX') === 'CAPEX') {
        totalCapex += fixedCost;
      } else {
        totalOpex += fixedCost;
      }
    });

    return {
      totalCapex: Math.round(totalCapex), 
      totalOpex: Math.round(totalOpex) 
    };
  }, [formData?.milestones, formData?.fixedCosts]);

  // Calculate milestone totals
  const calculateMilestoneTotals = useCallback((milestone) => {
    let capex = 0;
    let opex = 0;

    // Calculate role costs
    (milestone.roles || []).forEach(role => {
      const monthlyRate = role.rate || 0;
      const allocation = Math.max(0, Math.min(100, role.allocation || 0)) / 100;
      const months = Math.max(1, role.overlap_months || 1);
      const roleCost = monthlyRate * allocation * months;

      if (isNaN(roleCost) || roleCost < 0) return;

      if (role.capex_classification === 'CAPEX') {
        capex += roleCost;
      } else {
        opex += roleCost;
      }
    });

    // Add associated fixed costs
    const associatedFixedCosts = (formData?.fixedCosts || []).filter(cost => cost.milestone_id === milestone.id);
    associatedFixedCosts.forEach(cost => {
      const startDate = dayjs(cost.start_date + '-01');
      const endDate = dayjs(cost.end_date + '-01');
      const duration = Math.max(1, endDate.diff(startDate, 'month') + 1);
      const fixedCost = (cost.monthlyCost || 0) * duration;

      if (isNaN(fixedCost) || fixedCost < 0) return;

      if ((cost.capex_classification || 'OPEX') === 'CAPEX') {
        capex += fixedCost;
      } else {
        opex += fixedCost;
      }
    });

    return {
      capex: Math.round(capex),
      opex: Math.round(opex),
      total: Math.round(capex + opex)
    };
  }, [formData?.fixedCosts]);

  // Get member over allocation
  const getMemberOverAllocation = useCallback((memberId) => {
    let maxAllocation = 0;
    const milestoneAllocations = [];

    formData?.milestones?.forEach(milestone => {
      const memberRoles = (milestone.roles || []).filter(role => role.roster_member_id === memberId);
      if (memberRoles.length === 0) return;

      // Build per-month allocation array so sequential roles don't falsely sum
      const monthlyAllocations = Array(12).fill(0);
      memberRoles.forEach(role => {
        const allocation = role.allocation || 0;
        if (role.dateRanges && role.dateRanges.length > 0) {
          role.dateRanges.forEach(dr => {
            const rangeAllocation = dr.allocation != null ? dr.allocation : allocation;
            for (let month = dr.startMonth; month <= dr.endMonth; month++) {
              const idx = month - 1;
              if (idx >= 0 && idx < 12) {
                monthlyAllocations[idx] += rangeAllocation;
              }
            }
          });
        } else {
          const months = role.overlap_months || 1;
          for (let i = 0; i < Math.min(months, 12); i++) {
            monthlyAllocations[i] += allocation;
          }
        }
      });

      const milestonePeak = Math.max(...monthlyAllocations);
      if (milestonePeak > 0) {
        milestoneAllocations.push({
          milestoneName: milestone.name,
          allocation: milestonePeak
        });
        maxAllocation = Math.max(maxAllocation, milestonePeak);
      }
    });

    return {
      hasOverAllocation: maxAllocation > 100,
      maxAllocation,
      milestoneAllocations
    };
  }, [formData?.milestones]);

  // Apply COLA to role
  const applyColaToRole = useCallback(async (role, milestone) => {
    const cacheKey = `${role.id}-${role.company}-${role.location}-${role.role}`;
    
    if (calculationCache.has(cacheKey)) {
      return calculationCache.get(cacheKey);
    }

    try {
      const roleProjectYear = getRoleProjectYear(role, milestone, outcomeData, formData, new Date().getFullYear());
      
      let rateInfo;
      if (role.roster_member_id) {
        const member = itRoster.find(m => m.id === role.roster_member_id);
        if (member) {
          const memberSalaryInfo = await calculateMemberSalary(member, roleProjectYear);
          if (memberSalaryInfo.monthlySalary > 0) {
            rateInfo = {
              monthlyRate: memberSalaryInfo.monthlySalary,
              isAdjusted: memberSalaryInfo.isAdjusted,
              adjustmentInfo: memberSalaryInfo.adjustmentInfo
            };
          }
        }
      } else {
        rateInfo = await getRateForRole(
          role.company || 'CA',
          role.location || 'US',
          role.role || '',
          roleProjectYear
        );
      }

      const result = {
        rate: rateInfo?.monthlyRate || role.rate || 0,
        rateAdjustmentInfo: rateInfo?.isAdjusted ? rateInfo.adjustmentInfo : null,
        calculatedForYear: roleProjectYear
      };

      // Cache the result
      setCalculationCache(prev => new Map(prev.set(cacheKey, result)));
      
      return result;
    } catch (error) {
      console.warn(`Could not apply COLA to role ${role.name}:`, error);
      return {
        rate: role.rate || 0,
        rateAdjustmentInfo: null,
        calculatedForYear: null
      };
    }
  }, [calculationCache, outcomeData, formData, itRoster]);

  // Get role timeline display for milestone roles - supports both calendar and project-relative formats
  const getRoleTimelineDisplay = useCallback((role, milestone) => {
    if (!milestone || !role) {
      return { display: 'No timeline', allocation: '0%' };
    }

    // Always use role's dateRanges if they exist, regardless of useDefaultTimeline flag
    if (role.dateRanges && role.dateRanges.length > 0) {
      const range = role.dateRanges[0];
      let roleStartDate, roleEndDate;

      // Detect format based on data characteristics and version markers
      const isCalendarFormat = (
        range.timelineVersion === '3.0' || // Explicit new format marker
        !formData?.start_month || // No project context available
        (range.startMonth >= 1 && range.startMonth <= 12 && 
         range.endMonth >= 1 && range.endMonth <= 12 &&
         range.startMonth <= range.endMonth && 
         !range.useProjectRelative) // Reasonable calendar months without project-relative flag
      );

      if (isCalendarFormat) {
        // New format: Direct calendar months (1-12)
        const startMonth = Math.max(1, Math.min(12, range.startMonth || 1));
        const endMonth = Math.max(startMonth, Math.min(12, range.endMonth || startMonth));
        // Create dates for the current year to display
        const currentYear = new Date().getFullYear();
        roleStartDate = dayjs().year(currentYear).month(startMonth - 1).startOf('month');
        roleEndDate = dayjs().year(currentYear).month(endMonth - 1).startOf('month');
      } else {
        // Legacy format: Project-relative months - convert to calendar dates
        const projectStart = dayjs(formData.start_month + '-01');
        roleStartDate = projectStart.add((range.startMonth || 1) - 1, 'month');
        roleEndDate = projectStart.add((range.endMonth || 1) - 1, 'month');
      }

      // Calculate actual duration from the dates
      const duration = roleEndDate.diff(roleStartDate, 'month') + 1;

      return {
        display: `${roleStartDate.format('MMM YYYY')} - ${roleEndDate.format('MMM YYYY')} (${Math.max(1, duration)}mo)`,
        allocation: `${role.allocation || 0}%`
      };
    } else {
      // Fallback to milestone timeline if no dateRanges exist
      const milestoneStart = dayjs(milestone.start_date);
      const milestoneEnd = dayjs(milestone.end_date);
      // Calculate actual duration from milestone dates
      const duration = milestoneEnd.diff(milestoneStart, 'month') + 1;

      return {
        display: `${milestoneStart.format('MMM YYYY')} - ${milestoneEnd.format('MMM YYYY')} (${Math.max(1, duration)}mo)`,
        allocation: `${role.allocation || 0}%`
      };
    }
  }, [formData?.start_month]);

  // Calculate fixed costs total
  const calculateFixedCostsTotal = useMemo(() => {
    return (formData?.fixedCosts || []).reduce((sum, cost) => {
      const startDate = dayjs(cost.start_date + '-01');
      const endDate = dayjs(cost.end_date + '-01');
      const duration = Math.max(1, endDate.diff(startDate, 'month') + 1);
      const fixedCost = (cost.monthlyCost || 0) * duration;

      if (isNaN(fixedCost) || fixedCost < 0) {
        return sum;
      }

      return sum + fixedCost;
    }, 0);
  }, [formData?.fixedCosts]);

  // Get approved budget comparison
  const approvedBudgetComparison = useMemo(() => {
    if (!outcomeData?.approved_budget || 
        (outcomeData.approved_budget.capex === 0 && outcomeData.approved_budget.opex === 0)) {
      return null;
    }

    const { totalCapex, totalOpex } = calculateCapexOpexTotals;
    const totalStaffed = totalCapex + totalOpex;
    const approvedCapex = outcomeData.approved_budget.capex || 0;
    const approvedOpex = outcomeData.approved_budget.opex || 0;
    const totalApproved = approvedCapex + approvedOpex;

    return {
      staffedCapex: totalCapex,
      staffedOpex: totalOpex,
      totalStaffed,
      approvedCapex,
      approvedOpex,
      totalApproved,
      capexVariance: totalCapex - approvedCapex,
      opexVariance: totalOpex - approvedOpex,
      totalVariance: totalStaffed - totalApproved,
      isOverBudget: totalStaffed > totalApproved,
      hasApprovedBudget: true
    };
  }, [calculateCapexOpexTotals, outcomeData?.approved_budget]);

  // Clear calculation cache
  const clearCalculationCache = useCallback(() => {
    setCalculationCache(new Map());
  }, []);

  return {
    // Calculations
    calculateCapexOpexTotals,
    calculateMilestoneTotals,
    getMemberOverAllocation,
    applyColaToRole,
    getRoleTimelineDisplay,
    calculateFixedCostsTotal,
    approvedBudgetComparison,
    
    // Helpers
    getInitialStartMonth,
    getInitialAdjustedMonths,
    
    // Cache management
    clearCalculationCache
  };
};